import { useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconArrowBackUp, IconClipboardList } from '@tabler/icons-react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ProductionRunStatus, RunMode } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { EmptyState } from '../../../components/EmptyState';
import { useReverseAndCloneRun, useRuns, useStages } from '../api';
import { formatDateTime, RUN_MODE_LABELS } from '../format';
import type { RunDto } from '../types';
import { YieldVarianceBadge } from './YieldVarianceBadge';

function RunRowActions({ run }: { run: RunDto }) {
  const canOperate = useCanOperate();
  const navigate = useNavigate();
  const reverseClone = useReverseAndCloneRun(run.id);
  const [error, setError] = useState<string | null>(null);

  async function handleReverseClone() {
    setError(null);
    try {
      const result = await reverseClone.mutateAsync();
      navigate({ to: '/production-packconv/runs/$runId', params: { runId: result.newRunId } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this run');
    }
  }

  return (
    <Group gap="xs" wrap="nowrap">
      <Button
        size="xs"
        variant="light"
        // `Button`'s `component` prop doesn't narrow extra props like a strictly
        // route-typed `to`/`params` (same limitation `_authenticated/silo-counts.
        // tsx`'s header documents for `Tabs.Tab`) — `renderRoot` is Mantine's
        // supported escape hatch: it receives the fully-assembled DOM/ARIA props to
        // spread onto an arbitrary root element, here a real `<Link>` whose own
        // typed signature checks `to`/`params` correctly.
        renderRoot={(props) => (
          <Link to="/production-packconv/runs/$runId" params={{ runId: run.id }} {...props} />
        )}
      >
        {run.status === 'OPEN' || run.status === 'CLOSED' ? 'Continue' : 'View'}
      </Button>
      {run.status === 'POSTED' && (
        <Stack gap={2}>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <ConfirmButton
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconArrowBackUp size={14} />}
                loading={reverseClone.isPending}
                disabled={!canOperate}
                label="Reverse & clone"
                message={`Reverse run ${run.number}? Its input/output/consumable stock movements and yield will be backed out by a reversing ledger entry, and a new DRAFT run will be created for you to redo.`}
                onConfirm={handleReverseClone}
              />
            </span>
          </Tooltip>
          {error && (
            <Text size="xs" c="red">
              {error}
            </Text>
          )}
        </Stack>
      )}
    </Group>
  );
}

/** Runs register (BUILD_SPEC.md §11: "registers ... runs"), reused by both the
 * all-runs page (`/production-packconv/runs`) and the kiln-batches page
 * (`/production-packconv/batches`, filtered to `runMode="BATCH"`). Posted runs
 * offer Reverse &amp; clone (plan.md QC fix #10). */
export function RunsTable({ runMode, title }: { runMode?: RunMode; title: string }) {
  const [status, setStatus] = useState<ProductionRunStatus | null>(null);
  const runsQuery = useRuns({ status: status ?? undefined });
  const stagesQuery = useStages();
  const stagesById = useMemo(() => new Map((stagesQuery.data?.items ?? []).map((s) => [s.id, s])), [stagesQuery.data]);

  const rows = useMemo(() => {
    const items = runsQuery.data?.items ?? [];
    const filtered = runMode ? items.filter((r) => r.runMode === runMode) : items;
    return [...filtered].reverse();
  }, [runsQuery.data, runMode]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          {title}
        </Text>
        <Select
          placeholder="All statuses"
          clearable
          data={['OPEN', 'CLOSED', 'POSTED', 'REVERSED']}
          value={status}
          onChange={(v) => setStatus(v as ProductionRunStatus | null)}
          w={160}
        />
      </Group>
      {runsQuery.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load runs.
        </Alert>
      )}
      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Stage</Table.Th>
                <Table.Th>
                  <Tooltip label="Batch: a single kiln load, cages in to cages out. Continuous: a shift's steady inputs and outputs.">
                    <span>Mode</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th>Shift / started</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Yield variance</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((run) => (
                <Table.Tr key={run.id}>
                  <Table.Td>{run.number}</Table.Td>
                  <Table.Td>{stagesById.get(run.stageId)?.name ?? run.stageId}</Table.Td>
                  <Table.Td>{RUN_MODE_LABELS[run.runMode]}</Table.Td>
                  <Table.Td>{run.shiftLabel ?? formatDateTime(run.startedAt)}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={run.status} domain="productionRun" />
                  </Table.Td>
                  <Table.Td>
                    {run.yieldVariancePct !== null ? <YieldVarianceBadge variancePct={run.yieldVariancePct} /> : '—'}
                  </Table.Td>
                  <Table.Td>
                    <RunRowActions run={run} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!runsQuery.isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <EmptyState
                      icon={IconClipboardList}
                      title={runMode === 'BATCH' ? 'No kiln batches yet' : 'No runs yet'}
                      hint={
                        runMode === 'BATCH'
                          ? "Use 'Open kiln batch' above to start one."
                          : 'Start one from Record a shift, or open a kiln batch.'
                      }
                      action={
                        runMode !== 'BATCH' ? (
                          <Button
                            component={Link}
                            to="/production-packconv"
                            size="xs"
                            variant="light"
                          >
                            Record a shift
                          </Button>
                        ) : undefined
                      }
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
